<template>
    <div>
        <div class="filters row no-gutters" :class="{ 'compare-open': compareOpen }">
            <div class="filter-column">
                <select-input
                    class="filter"
                    placeholder="Units"
                    :options="series"
                    @input="value => viewsUpdate('Series' , value)"
                    :searchable="false"
                    :value="views.Series"
                    inputId="Series"
                >
                </select-input>
            </div>

            <div class="filter-column" v-for="category in uniqueCategories" :key="category">
                <select-input
                    class="filter"
                    :placeholder="category"
                    :options="uniqueCategoryValues[category]"
                    @input="value => filterUpdate(`${category}`, value)"
                    :value="filters[category]"
                    :searchable="false"
                    :inputId="category"
                    :disabled="category === 'Creative' && !filters.Placement"
                    :passThroughClass="category"
                >
                </select-input>
            </div>
        </div>

        <div class="reset-container">
            <button class="reset-button" @click="reset">Reset</button>
        </div>

        <div class="chart">
            <div class="controls row no-gutters">
                <div class="control-column">
                    <select-input
                        class="filter"
                        placeholder="By time/Ad unit"
                        :options="uniqueCategories"
                        @input="value => viewsUpdate('XValue' , value)"
                        :value="views.XValue"
                        :searchable="false"
                        inputId="XValue"
                    >
                    </select-input>
                </div>
                <div class="control-column">
                    <select-input
                        class="filter"
                        placeholder="Date group"
                        :options="dateGroupList"
                        @input="value => viewsUpdate('dateGroup' , value)"
                        :value="views.dateGroup"
                        :searchable="false"
                        inputId="dateGroup"
                    >
                    </select-input>
                </div>
            </div>

            <div v-show="unselectedXAxis" class="no-data-text text-center font-arial">Please select a value for "By time/Ad unit"</div>

            <div v-show="!unselectedXAxis" class="toggle-buttons">
                <button @click="toTable" :class="{ selected: showTable }" title="Change to table view">
                    <div class="icon icon--table"></div>
                    <span class="visually-hidden">Change To Table</span>
                </button>
                <button @click="toChart" :class="{ selected: !showTable }" title="Change to chart view">
                    <div class="icon icon--graph"></div>
                    <span class="visually-hidden">Change To Chart</span>
                </button>
            </div>

            <line-chart class="chart-wrapper"  v-show="!unselectedXAxis" v-if="!showTable" :views="views" :uniqueCategories="uniqueCategories" :filters="filters" :series="series"></line-chart>
            <table-chart class="chart-wrapper" v-show="!unselectedXAxis" v-if="showTable" :views="views" :uniqueCategories="uniqueCategories" :filters="filters" :series="series"></table-chart>
        </div>
    </div>
</template>

<script>
    import Moment from 'moment';

    import LineChart from '@/components/tiles/LineChart';
    import TableChart from '@/components/tiles/TableChart';

    import SelectInput from '@/components/filters/SelectInput';
    import store from '@/store';

    export default {
        components: {
            LineChart,
            TableChart,
            SelectInput,
        },
        props: {
            updateFilters: {
                default: false,
                type: Boolean,
            },
        },
        data() {
            return {
                showTable: false,
                unselectedXAxis: false,
                uniqueCategories: [],
                uniqueCategoryValues: {},
                series: [],
                filters: Object.assign({}, store.state.chartFilters),
                views: Object.assign({}, store.state.chartViews),
                dateGroupList: [],
            };
        },
        methods: {
            reset() {
                this.uniqueCategories.forEach((name) => {
                    if (this.updateFilters) {
                        store.dispatch('updateChartFilters', {
                            name, value: null,
                        });
                    }

                    this.$set(this.filters, name, null);
                });

                this.$set(this.views, 'Series', null);
                this.$set(this.views, 'XValue', 'Date');
                this.$set(this.views, 'dateGroup', 'Days');
            },
            toChart() {
                this.showTable = false;
            },
            toTable() {
                this.showTable = true;
            },
            filterUpdate(name, value) {
                this.filters[name] = value;

                if (this.updateFilters) {
                    store.dispatch('updateChartFilters', {
                        name, value,
                    });
                }

                if (name === 'Date' && value !== null) {
                    if (this.views.XValue === 'Date') {
                        this.viewsUpdate('XValue', null);
                    }
                }

                this.createCategories();
            },
            viewsUpdate(name, value) {
                if (name === 'XValue' && value === null) {
                    this.unselectedXAxis = true;
                } else if (name === 'XValue') {
                    this.unselectedXAxis = false;
                }

                this.views[name] = value;

                if (this.updateFilters) {
                    store.dispatch('updateChartViews', {
                        name, value,
                    });
                }

                this.createCategories();
            },
            createCategories() {
                this.dateGroupList = [];
                this.uniqueCategoryValues = [];

                const uniqueDays = [];
                const uniqueWeeks = [];
                const uniqueMonths = [];

                // let test = 0;
                this.jsonData.forEach((datum) => {
                    Object.keys(datum.categories).forEach((category) => {
                        const categoryObject = datum.categories[category];
                        // test += 1;
                        if (category === 'Date') {
                            const date = categoryObject;
                            const week = Moment(date, 'YYYY-MM-DD').format('W YYYY');
                            const month = Moment(date, 'YYYY-MM-DD').format('MMMM YYYY');

                            if (!uniqueDays.includes(date)) {
                                uniqueDays.push(date);
                            }

                            if (!uniqueWeeks.includes(week)) {
                                uniqueWeeks.push(week);
                            }

                            if (!uniqueMonths.includes(month)) {
                                uniqueMonths.push(month);
                            }
                        }

                        if (!this.uniqueCategories.includes(category)) {
                            this.uniqueCategories.push(category);
                        }

                        let changedCategory = categoryObject;

                        if (category === 'Date') {
                            if (this.views.dateGroup === 'Weeks') {
                                changedCategory = Moment(changedCategory, 'YYYY-MM-DD').format('W YYYY');
                            } else if (this.views.dateGroup === 'Months') {
                                changedCategory = Moment(changedCategory, 'YYYY-MM-DD').format('MMMM YYYY');
                            }
                        }

                        if (!Object.keys(this.uniqueCategoryValues).includes(category)) {
                            this.uniqueCategoryValues[category] = [];
                            // eslint-disable-next-line
                        } else if (!this.uniqueCategoryValues[category].includes(changedCategory)) {
                            if (category === 'Creative' && store.state.chartFilters.Placement) {
                                if (datum.categories.Placement === store.state.chartFilters.Placement) {
                                    // eslint-disable-next-line
                                    this.uniqueCategoryValues[category].push(changedCategory);
                                }
                            } else {
                                this.uniqueCategoryValues[category].push(changedCategory);
                            }
                        }
                    });

                    Object.keys(datum.values).forEach((value) => {
                        if (!this.series.includes(value)) {
                            this.series.push(value);
                        }
                    });
                });

                this.dateGroupList.push('Days');

                if (uniqueWeeks.length > 1) {
                    this.dateGroupList.push('Weeks');
                }

                if (uniqueMonths.length > 1) {
                    this.dateGroupList.push('Months');
                }

                if (!this.series.includes('CTR %')) {
                    this.series.push('CTR %');
                }

                this.uniqueCategories.forEach((cat) => {
                    if (!Object.keys(this.filters).includes(cat)) {
                        const cateName = `${cat}`;
                        this.$set(this.filters, cateName, null);
                    }
                });
            },
        },
        computed: {
            jsonData() {
                return store.state.chartData.data;
            },
            compareOpen() {
                return store.state.compareChartOpen[store.state.currentTab];
            },
        },
        mounted() {
            this.createCategories();
        },
    };
</script>

<style lang="scss" scoped>
    @import '../assets/scss/partials/variables';

    .filters {
        .filter {
            @media screen and (min-width: $tablet) {
                margin-bottom: 3px;
            }
        }

        .filter-column {
            flex-basis: 100%;
            margin-right: 3px;

            max-width: 200px;

            transition: flex-basis 0.5s ease;

            @media screen and (min-width: $tablet) {
                flex-basis: auto;
                max-width: none;
                min-width: 200px;
            }

            &:not(:nth-child(1)) {
                display: none;

                @media screen and (min-width: $tablet) {
                    display: block;
                }
            }
        }

        &.compare-open {
            .filter-column {
                @media screen and (min-width: $tablet) {
                    flex-basis: auto;
                }
            }
        }
    }

    .controls {
        position: absolute;
        top: 0;
        left: 0;

        .control-column {
            flex-basis: 100%;
            margin-right: 3px;

            max-width: 200px;

            transition: flex-basis 0.5s ease;

            @media screen and (min-width: $tablet) {
                flex-basis: auto;
                max-width: none;
                min-width: 200px;
            }

            &:not(:nth-child(1)) {
                display: none;

                @media screen and (min-width: $tablet) {
                    display: block;
                }
            }
        }

        &.compare-open {
            .filter-column {
                @media screen and (min-width: $tablet) {
                    flex-basis: auto;
                }
            }
        }
    }

    .toggle-buttons {
        position: absolute;
        top: 0;
        right: 0;

        button {
            $size: 35px;
            width: $size;
            height: $size;
            background-color: $kpmg-blue;

            cursor: pointer;

            &.selected {
                background-color: $kpmg-purple;
            }
        }
    }

    .chart {
        position: relative;

        margin: 0 -#{$mobile-side-gutter};

        padding: 25px 0 41px $mobile-side-gutter;

        background-color: white;

        border-top-style: solid;
        border-top-width: 3px;
        border-top-color: $kpmg-blue;

        z-index: 0;

        @media screen and (min-width: $tablet) {
            margin: 0;
            padding-top: 30px;
        }

        &-wrapper {
            padding-top: 78px;

            @media screen and (min-width: $tablet) {
                padding-top: 92px;
            }
        }
    }

    .no-data-text {
        padding-right: 30px;

        @media screen and (min-width: $tablet) {
            padding-top: 41px;
        }
    }

    .reset-container {
        position: relative;
    }

    .reset-button {
        height: 40px;
        margin-bottom: 3px;
        padding: 12px 0 11px 0;

        color: $kpmg-blue;
        font-size: 12px;
        letter-spacing: 0.6px;
        cursor: pointer;

        @media screen and (min-width: $tablet) {
            padding: 11px 20px 10px 0;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/ToggleChartContainer.vue